import { UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import DatePicker from '@/components/basicComponents/datePicker';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormFieldsWrapper } from '..';
import Dialog, { DialogStatus } from '../modals/dialog';
import { extractDirtyFields } from '../utils';

interface IForm {
  investorDocsDue: string | null;
  nextClose: string | null;
  finalClose: string | null;
}

const defaultFormValues: IForm = {
  investorDocsDue: null,
  nextClose: null,
  finalClose: null
};

interface ClosingsModalPageProps {
  title: string;
  investmentId?: string;
  formData?: IForm | {};
  investmentRefetch: () => void;
  handleCloseEditModalPage: () => void;
}

export const ClosingsModalPage = ({ title, investmentId, formData = {}, investmentRefetch, handleCloseEditModalPage }: ClosingsModalPageProps) => {
  const { isMobile } = useResponsive();
  const {
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogStatus, setDialogStatus] = useState<DialogStatus>();

  const [updateInvestment] = useMutation(UPDATE_INVESTMENT_MUTATION);

  useEffect(() => {
    if (!investmentId) return;

    reset((defaultFormData) => ({ ...defaultFormData, ...formData }));
  }, [formData]);

  const onCloseDialog = () => setDialogOpen(false);
  const onDialogSuccess = () => {
    reset();
    onCloseDialog();
    handleCloseEditModalPage();
  };

  const onUpdateInvestment = async (data: IForm) => {
    if (!investmentId) return;
    try {
      setDialogStatus(DialogStatus.LOADING);
      setDialogOpen(true);
      const dirtyData = extractDirtyFields(data, dirtyFields);

      await updateInvestment({
        variables: {
          data: {
            id: investmentId,
            ...dirtyData
          }
        }
      });

      investmentRefetch();
      setDialogStatus(DialogStatus.SUCCESS);
      setDialogOpen(true);
    } catch (error) {
      setDialogStatus(DialogStatus.FAILED);
      setDialogOpen(true);
    }
  };

  return (
    <MainWrap>
      <Dialog
        isOpen={isDialogOpen}
        onClose={onCloseDialog}
        onSuccess={onDialogSuccess}
        reFetch={() => onUpdateInvestment(getValues())}
        status={dialogStatus}
      />
      <Header modalControl={<GoBackButton handleClose={handleCloseEditModalPage} backToTitle={''} />} />
      <PageTitle title={title ?? ''} />
      <PaddingWrap>
        <Form
          id="opportunitiesForm"
          onSubmit={handleSubmit(onUpdateInvestment, (d) => {
            console.log(d);
            getValues();

            window.scrollTo(0, 0);
          })}
        >
          <FormFieldsWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="investorDocsDue"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DatePicker
                    label="Investor Docs Due"
                    value={value || ''}
                    onChange={(date) => {
                      onChange(date || '');
                    }}
                    errorText={error?.message}
                    tooltip="Provide advisors with a due date for investor docs."
                    size="md"
                    ignoreLocalization={true}
                  />
                )}
              />
              <Controller
                name="nextClose"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DatePicker
                    label="Next Close"
                    value={value || ''}
                    onChange={(date) => {
                      onChange(date || '');
                    }}
                    errorText={error?.message}
                    tooltip="Set the Next Close date that you wish to track.  You can edit this date at any time."
                    tooltipWidth={300}
                    size="md"
                    ignoreLocalization={true}
                  />
                )}
              />
            </TwoColumnWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="finalClose"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DatePicker
                    label="Final Close"
                    value={value || ''}
                    onChange={(date) => {
                      onChange(date || '');
                    }}
                    errorText={error?.message}
                    tooltip="Set the Final Close date that you wish to track.  You can edit this date at any time."
                    tooltipWidth={300}
                    size="md"
                    ignoreLocalization={true}
                  />
                )}
              />
            </TwoColumnWrapper>
          </FormFieldsWrapper>
          <FormFooter onCancel={handleCloseEditModalPage} disableSaveButton={false} showRequiredFieldsWarning={false} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;
